import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { photoAPI, nomineeAPI } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  text-align: center;
  color: #1f2937;
  margin-bottom: 2rem;
`;

const Select = styled.select`
  margin: 1rem 0;
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  width: 100%;
  fontSize: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const FileInput = styled.input`
  margin: 1rem 0;
  padding: 0.5rem;
  border: 2px dashed #667eea;
  border-radius: 8px;
  width: 100%;
`;

const Button = styled.button`
  background: #667eea;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  fontWeight: 600;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #5a67d8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #cbd5e0;
    color: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button`
  background: #667eea;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

interface Nominee {
  id: number;
  name: string;
  room_id: number;
  creator_name: string;
}

const PhotoUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [nominees, setNominees] = useState<Nominee[]>([]);
  const [selectedNomineeId, setSelectedNomineeId] = useState<string>('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const roomId = searchParams.get('room_id');

  const loadNominees = useCallback(async () => {
    if (!roomId) return;
    try {
      const data = await nomineeAPI.getRoomNominees(parseInt(roomId));
      setNominees(data);
    } catch (error) {
      console.error('Error loading nominees:', error);
      alert('Error al cargar nominados');
    }
  }, [roomId]);

  useEffect(() => {
    if (roomId) {
      loadNominees();
    }
  }, [roomId, loadNominees]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !selectedNomineeId || !roomId) {
      alert('Por favor selecciona un archivo y un nominado');
      return;
    }

    setLoading(true);
    try {
      await photoAPI.uploadPhoto(parseInt(roomId), parseInt(selectedNomineeId), file);
      alert('¡Foto subida exitosamente!');
      navigate(`/dashboard?room_id=${roomId}`);
    } catch (error) {
      console.error('Error subiendo foto:', error);
      alert('Error al subir la foto. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!roomId) {
    return (
      <Container>
        <Card>
          <Title>Error</Title>
          <p>No se especificó la sala. Por favor regresa al dashboard.</p>
          <BackButton onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </BackButton>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <BackButton onClick={() => navigate(`/dashboard?room_id=${roomId}`)}>
          ← Volver al Dashboard
        </BackButton>
        
        <Title>📸 Subir Foto</Title>
        
        <form onSubmit={handleSubmit}>
          <p>Sube una foto para un nominado:</p>
          
          <Select
            value={selectedNomineeId}
            onChange={(e) => setSelectedNomineeId(e.target.value)}
            required
          >
            <option value="">Selecciona un nominado</option>
            {nominees.map(nominee => (
              <option key={nominee.id} value={nominee.id}>
                {nominee.name} (propuesto por {nominee.creator_name})
              </option>
            ))}
          </Select>
          
          <FileInput
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required
          />
          
          {file && (
            <p>Archivo seleccionado: {file.name}</p>
          )}
          
          <Button type="submit" disabled={loading || !file || !selectedNomineeId}>
            {loading ? 'Subiendo...' : 'Subir Foto'}
          </Button>
        </form>
      </Card>
    </Container>
  );
};

export default PhotoUpload;